<script setup lang="ts">
import { useVModel } from '@vueuse/core'

import { offerRewardTypeBlockKey } from '@/components/rewards/utils/offerRewardTypeBlockContext'
import { resetRewardFieldsOnTypeChange } from '@/components/rewards/utils/rewardFormInitialization'
import { getCartMinimumAmountEuros, setCartMinimumAmountEuros } from '@/components/rewards/utils/rewardAccessConditions'
import {
  validateReductionValue,
  validateXForYBuyQuantity,
  validateXForYFreeQuantity
} from '@/components/rewards/utils/offerRewardValidation'
import { getOfferRewardVouchers, getRewardTypeIcon, getRewardVouchers } from '@/components/rewards/utils/rewardHelpers'
import { slugBelongsTo } from '@/components/rewards/utils/rewardTypeRegistry'
import { switchOfferDiscountRewardTypeForScope } from '@/utils/offerHelpers'
import { addProductIfNotExists, removeProductFromList } from '@/utils/productHelpers'

import ProductAssociationBlock from '@/components/product/ProductAssociationBlock.vue'
import RewardFormBlock from '@/components/rewards/RewardFormBlock.vue'

import type { RadioCardOption } from '@/components/ui/AppRadioCard.vue'
import type AppFormInput from '@/components/ui/AppFormInput.vue'
import type { ApplyReductionOn } from '@/types/offer'
import type { LoyaltyReward, PartnerProduct } from '@/types/types'
import { useRewardForm, type RewardFormErrorField } from './compmosable/useRewardForm'

const props = withDefaults(
  defineProps<{
    editingReward: LoyaltyReward
  }>(),
  {}
)

const emit = defineEmits<{
  (e: 'update:editingReward', value: LoyaltyReward): void
}>()

const editingReward = useVModel(props, 'editingReward', emit)

const offerCtx = inject(offerRewardTypeBlockKey, null)

const localFormTouched = ref(false)
const formTouched = offerCtx?.formTouched ?? localFormTouched

const { t } = useI18n()
const router = useRouter()
const { rewardVouchers, isProductFeatureDisabled, productCatalogPartners, isLoadingPartners, disabledReason } =
  useRewardForm()

const rewardTypeIdsMap = computed(() => {
  if (rewardVouchers.value.length === 0) return null
  return rewardVouchers.value.reduce<Record<string, number>>((acc, v) => {
    acc[v.slug] = v.id
    return acc
  }, {})
})

const rewardTypeIdsBySlug = computed(() => offerCtx?.rewardTypeIdsBySlug.value ?? rewardTypeIdsMap.value)

const FREE_DELIVERY_SLUG = 'voucher_free_delivery'

const isFreeDeliveryReward = computed(() => editingReward.value?.rewardType?.slug === FREE_DELIVERY_SLUG)

const offerRewardTypes = computed(() => {
  const order = [
    'free_product',
    'voucher_x_for_y',
    'voucher_discount_percent',
    'voucher_discount_amount',
    FREE_DELIVERY_SLUG
  ]
  if (!rewardTypeIdsBySlug.value) return []

  const types = getOfferRewardVouchers(t, rewardTypeIdsBySlug.value)

  if (!isFreeDeliveryReward.value) {
    return types.sort((a, b) => order.indexOf(a.slug) - order.indexOf(b.slug))
  }

  const freeDeliveryVoucher = getRewardVouchers(t, rewardTypeIdsBySlug.value).find(
    (voucher) => voucher.slug === FREE_DELIVERY_SLUG
  )
  if (!freeDeliveryVoucher || types.some((voucher) => voucher.slug === FREE_DELIVERY_SLUG)) {
    return types.sort((a, b) => order.indexOf(a.slug) - order.indexOf(b.slug))
  }

  return [
    ...types,
    {
      ...freeDeliveryVoucher,
      example: t('pages.offer.reward.typeDesc.freeDelivery')
    }
  ].sort((a, b) => order.indexOf(a.slug) - order.indexOf(b.slug))
})

const isDiscountType = computed(() => slugBelongsTo('discount', editingReward.value?.rewardType?.slug))
const isPercentDiscount = computed(() => slugBelongsTo('percentDiscount', editingReward.value?.rewardType?.slug))
const isXForYReward = computed(() => slugBelongsTo('xForY', editingReward.value?.rewardType?.slug))
const isFreeProductReward = computed(() => slugBelongsTo('freeProduct', editingReward.value?.rewardType?.slug))

const isVoucherDisabled = (voucher: { slug: string }) =>
  voucher.slug === 'voucher_x_for_y' && isProductFeatureDisabled.value

const hasDisabledOfferRewardType = computed(() => offerRewardTypes.value.some((voucher) => isVoucherDisabled(voucher)))

const normalizeOfferRewardSlug = (slug?: string): string => {
  if (!slug) return ''
  if (slug === 'product_discount_amount') return 'voucher_discount_amount'
  if (slug === 'product_discount_percent') return 'voucher_discount_percent'
  return slug
}

const isOfferVoucherSelected = (voucherSlug: string): boolean =>
  normalizeOfferRewardSlug(editingReward.value?.rewardType?.slug) === normalizeOfferRewardSlug(voucherSlug)

const updateVoucher = (selectedId: number) => {
  const voucher = offerRewardTypes.value.find((v) => v.id === selectedId)
  if (!voucher || !editingReward.value) return

  const prevSlug = normalizeOfferRewardSlug(editingReward.value.rewardType?.slug)
  const nextSlug = normalizeOfferRewardSlug(voucher.slug)

  editingReward.value.rewardType = {
    id: voucher.id,
    slug: voucher.slug,
    name: voucher.text,
    isVoucher: voucher.isVoucher
  }

  if (prevSlug && nextSlug !== prevSlug) {
    if (offerCtx) {
      resetRewardFieldsOnTypeChange(editingReward.value)
    }
    offerCtx?.accessConditions?.resetOnRewardTypeChange?.()
  }

  formTouched.value = true
}

type OfferRewardTypeOption = RadioCardOption & { id: number; slug: string }

const offerRewardTypeOptions = computed<OfferRewardTypeOption[]>(() =>
  offerRewardTypes.value.map((voucher) => ({
    id: voucher.id,
    slug: voucher.slug,
    title: voucher.text,
    description: voucher.example,
    icon: getRewardTypeIcon({ slug: voucher.slug, name: voucher.text }),
    disabled: isVoucherDisabled(voucher)
  }))
)

const selectedOfferRewardTypeOption = computed({
  get: () => offerRewardTypeOptions.value.find((option) => isOfferVoucherSelected(option.slug)),
  set: (option: OfferRewardTypeOption | undefined) => {
    if (option?.id) updateVoucher(option.id)
  }
})

const reductionValueTouched = ref(false)
const xForYBuyQuantityTouched = ref(false)
const xForYFreeQuantityTouched = ref(false)
const freeDeliveryMinAmountTouched = ref(false)

const freeDeliveryMinAmount = computed<number | null>({
  get: () => getCartMinimumAmountEuros(editingReward.value),
  set: (val) => {
    if (!editingReward.value) return
    setCartMinimumAmountEuros(editingReward.value, val)
    formTouched.value = true
  }
})

const touchFreeDeliveryMinAmount = () => {
  freeDeliveryMinAmountTouched.value = true
  formTouched.value = true
}

watch(
  () => editingReward.value?.rewardType?.slug,
  () => {
    reductionValueTouched.value = false
    xForYBuyQuantityTouched.value = false
    xForYFreeQuantityTouched.value = false
    freeDeliveryMinAmountTouched.value = false
  }
)

const applyReductionOn = computed<ApplyReductionOn>({
  get: () => {
    const r = editingReward.value
    if (!r) return 'cart'
    if (r.applyReductionOn) return r.applyReductionOn

    return ['product_discount_percent', 'product_discount_amount'].includes(r?.rewardType?.slug) &&
      !isProductFeatureDisabled.value
      ? 'products'
      : 'cart'
  },
  set: (v) => {
    const r = editingReward.value
    if (!r) return
    switchOfferDiscountRewardTypeForScope(r, v, rewardTypeIdsBySlug.value)
    if (v === 'cart') {
      r.items = []
    }
    formTouched.value = true
  }
})

const xForYBuyQuantity = computed<number | null>({
  get: () => {
    if (!isXForYReward.value || editingReward.value?.value == null) return null
    return Math.trunc(Number(editingReward.value.value))
  },
  set: (val) => {
    if (!editingReward.value) return
    editingReward.value.value = val == null ? null : Math.trunc(Number(val))
    formTouched.value = true
  }
})

const xForYFreeQuantity = computed<number | null>({
  get: () => {
    if (!isXForYReward.value || editingReward.value?.voucherDiscountAmount == null) return null
    return Math.trunc(Number(editingReward.value.voucherDiscountAmount))
  },
  set: (val) => {
    if (!editingReward.value) return
    editingReward.value.voucherDiscountAmount = val == null ? null : Math.trunc(Number(val))
    formTouched.value = true
  }
})

const reductionLabel = computed(() =>
  isPercentDiscount.value ? t('pages.offer.reward.reductionPercentLabel') : t('pages.offer.reward.reductionAmountLabel')
)
const reductionInputId = computed(() => (isPercentDiscount.value ? 'offer-discountPercent' : 'offer-discountAmount'))
const reductionStep = computed(() => (isPercentDiscount.value ? 1 : 0.01))
const reductionSuffix = computed(() => (isPercentDiscount.value ? '%' : '€'))

const hasReductionValueError = computed(() => {
  if (!isDiscountType.value || !editingReward.value) return false
  return !validateReductionValue(editingReward.value.value, isPercentDiscount.value)
})

const reductionValueErrors = computed(() =>
  reductionValueTouched.value && hasReductionValueError.value ? [t('pages.offer.reward.reductionValueError')] : []
)

const hasXForYBuyQuantityError = computed(() => {
  if (!isXForYReward.value || !editingReward.value) return false
  return !validateXForYBuyQuantity(editingReward.value.value)
})

const hasXForYFreeQuantityError = computed(() => {
  if (!isXForYReward.value || !editingReward.value) return false
  return !validateXForYFreeQuantity(editingReward.value.voucherDiscountAmount)
})

const hasXForYItemsError = computed(() => {
  if (!isXForYReward.value || !editingReward.value) return false
  return (editingReward.value.items?.length ?? 0) === 0
})

const xForYBuyQuantityErrors = computed(() =>
  xForYBuyQuantityTouched.value && hasXForYBuyQuantityError.value ? [t('pages.offer.reward.xForYBuyQuantityError')] : []
)

const xForYFreeQuantityErrors = computed(() =>
  xForYFreeQuantityTouched.value && hasXForYFreeQuantityError.value
    ? [t('pages.offer.reward.xForYFreeQuantityError')]
    : []
)

const xForYSummaryHtml = computed(() => {
  if (!isXForYReward.value || !editingReward.value) return ''
  const buy = Math.trunc(Number(editingReward.value.value))
  const free = Math.trunc(Number(editingReward.value.voucherDiscountAmount))
  if (!Number.isFinite(buy) || !Number.isFinite(free) || buy <= 0 || free <= 0) return ''
  const buyKey = buy > 1 ? 'pages.offer.reward.xForYBuyPlural' : 'pages.offer.reward.xForYBuySingular'
  const freeKey = free > 1 ? 'pages.offer.reward.xForYFreePlural' : 'pages.offer.reward.xForYFreeSingular'

  const buyLabel = t(buyKey, { buy: `<strong>${buy}</strong>` })
  const freeLabel = t(freeKey, { free: `<strong>${free}</strong>` })

  return t('pages.offer.reward.xForYSummary', { buyLabel, freeLabel })
})

const hasDiscountTargetProductsError = computed(() => {
  if (!isDiscountType.value || !editingReward.value) return false
  if (applyReductionOn.value !== 'products') return false
  return (editingReward.value.items?.length ?? 0) === 0
})

const isProductScopeDisabled = computed(() => isProductFeatureDisabled.value)

type ApplyReductionOnOption = RadioCardOption & { id: ApplyReductionOn }

const applyReductionOnOptions = computed<ApplyReductionOnOption[]>(() => [
  {
    id: 'cart',
    title: t('pages.offer.reward.cartEntire'),
    description: t('pages.offer.reward.cartEntireDesc'),
    icon: 'IconoirCart'
  },
  {
    id: 'products',
    title: t('pages.offer.reward.targetProducts'),
    description: t('pages.offer.reward.targetProductsDesc'),
    icon: 'IconoirGift',
    disabled: isProductScopeDisabled.value
  }
])

const selectedApplyReductionOnOption = computed({
  get: () => applyReductionOnOptions.value.find((option) => option.id === applyReductionOn.value),
  set: (option: ApplyReductionOnOption | undefined) => {
    if (option?.id) applyReductionOn.value = option.id
  }
})

const touchReductionValue = () => {
  reductionValueTouched.value = true
  formTouched.value = true
}

const touchXForYBuyQuantity = () => {
  xForYBuyQuantityTouched.value = true
  formTouched.value = true
}

const touchXForYFreeQuantity = () => {
  xForYFreeQuantityTouched.value = true
  formTouched.value = true
}

const handleRemoveTargetProduct = (product: PartnerProduct) => {
  if (!editingReward.value) return
  editingReward.value.items ||= []
  removeProductFromList(editingReward.value.items, product)
  formTouched.value = true
}

const handleSelectTargetProduct = (product: PartnerProduct) => {
  if (!editingReward.value) return
  editingReward.value.items ||= []
  addProductIfNotExists(editingReward.value.items, product)
  formTouched.value = true
}

const navigateToIntegrations = () => router.push({ name: 'IntegrationList' })

const reductionInputRef = ref<InstanceType<typeof AppFormInput> | null>(null)
const xForYBuyQuantityRef = ref<InstanceType<typeof AppFormInput> | null>(null)
const xForYFreeQuantityRef = ref<InstanceType<typeof AppFormInput> | null>(null)
const freeDeliveryMinAmountRef = ref<InstanceType<typeof AppFormInput> | null>(null)
const discountTargetProductsRef = ref<HTMLElement | null>(null)
const xForYItemsRef = ref<HTMLElement | null>(null)

const scrollToSection = (sectionRef: HTMLElement | null) => {
  sectionRef?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

const focusError = (field: RewardFormErrorField): boolean => {
  switch (field) {
    case 'reductionValue':
      if (!hasReductionValueError.value) return false
      reductionValueTouched.value = true
      reductionInputRef.value?.focus()
      return true
    case 'discountTargetProducts':
      if (!hasDiscountTargetProductsError.value) return false
      scrollToSection(discountTargetProductsRef.value)
      return true
    case 'xForYBuyQuantity':
      if (!hasXForYBuyQuantityError.value) return false
      xForYBuyQuantityTouched.value = true
      xForYBuyQuantityRef.value?.focus()
      return true
    case 'xForYFreeQuantity':
      if (!hasXForYFreeQuantityError.value) return false
      xForYFreeQuantityTouched.value = true
      xForYFreeQuantityRef.value?.focus()
      return true
    case 'xForYItems':
      if (!hasXForYItemsError.value) return false
      scrollToSection(xForYItemsRef.value)
      return true
    default:
      return false
  }
}

defineExpose({ focusError })
</script>

<template>
  <div class="space-y-5">
    <RewardFormBlock :title="t('pages.offer.reward.typeLabel')">
      <div>
        <AppRadioCard
          v-if="offerRewardTypes.length > 0"
          v-model="selectedOfferRewardTypeOption"
          :options="offerRewardTypeOptions"
          icon-as-radio
          class="grid-cols-1 sm:grid-cols-2"
        />

        <BaseTip
          v-if="hasDisabledOfferRewardType"
          class="mt-3"
          severity="secondary"
          icon-name="IconoirInfoCircle"
          :text="t(`pages.offer.reward.xForYDisabledReason.${disabledReason}`)"
        />
      </div>

      <hr />

      <template v-if="isDiscountType">
        <CardTitle>{{ t('pages.offer.reward.reductionValueTitle') }}</CardTitle>

        <div class="space-y-4">
          <div>
            <label class="mb-2 flex items-center gap-1 text-sm font-medium text-foreground">
              {{ reductionLabel }}
              <span class="text-red-500">*</span>
            </label>
            <AppFormInput
              :id="reductionInputId"
              ref="reductionInputRef"
              v-model="editingReward.value"
              type="number"
              :step="reductionStep"
              :min="0"
              :max="isPercentDiscount ? 100 : undefined"
              :placeholder="isPercentDiscount ? '15' : '5'"
              :errors="reductionValueErrors"
              error-classes="!mt-1 !text-xs"
              @update:model-value="touchReductionValue"
            >
              <template #icon-right>
                <div class="flex h-5 w-5 items-center justify-center text-sm text-muted-foreground">
                  {{ reductionSuffix }}
                </div>
              </template>
            </AppFormInput>
          </div>

          <div>
            <label class="mb-2 flex items-center gap-1 text-sm font-medium text-foreground">
              {{ t('pages.offer.reward.applyReductionOn') }}
            </label>
            <AppRadioCard
              v-model="selectedApplyReductionOnOption"
              :options="applyReductionOnOptions"
              icon-as-radio
              class="grid-cols-2"
            />
            <div v-if="applyReductionOn === 'products'" ref="discountTargetProductsRef" class="mt-4 space-y-3">
              <p class="text-xs text-foreground-secondary">
                {{ t('pages.offer.reward.selectTargetProductsHint') }}
              </p>
              <ProductAssociationBlock
                v-model:associated-products="editingReward.items"
                :is-loading="isLoadingPartners"
                :partners="productCatalogPartners"
                :error-message="
                  hasDiscountTargetProductsError ? t('pages.offer.reward.targetProductsRequiredError') : null
                "
                @select-product="handleSelectTargetProduct"
                @remove-product="handleRemoveTargetProduct"
                @configure="navigateToIntegrations"
              />
            </div>
          </div>

          <BaseTip
            v-if="isProductScopeDisabled"
            severity="secondary"
            icon-name="IconoirInfoCircle"
            :text="t(`pages.offer.reward.productScopeDisabledReason.${disabledReason}`)"
          />
        </div>
      </template>

      <template v-if="isXForYReward">
        <CardTitle>{{ t('pages.offer.reward.xForYRuleTitle') }}</CardTitle>
        <div class="space-y-4">
          <BaseTip
            v-if="isProductFeatureDisabled"
            severity="secondary"
            icon-name="IconoirInfoCircle"
            :text="t(`pages.offer.reward.xForYDisabledReason.${disabledReason}`)"
          />
          <div>
            <label class="mb-2 flex items-center gap-1 text-sm font-medium text-foreground">
              {{ t('pages.offer.reward.xForYProductsLabel') }}
            </label>
            <p class="text-xs text-foreground-secondary">
              {{ t('pages.offer.reward.xForYProductsHint') }}
            </p>
          </div>

          <div
            class="grid grid-cols-1 gap-3 sm:grid-cols-2"
            :class="isProductFeatureDisabled ? 'opacity-50 cursor-not-allowed' : ''"
          >
            <div>
              <label class="mb-1 flex items-center gap-1 text-xs font-medium text-foreground">
                {{ t('pages.offer.reward.xForYBuyQuantityLabel') }}
                <span class="text-red-500">*</span>
              </label>
              <AppFormInput
                id="offer-xForY-buyQuantity"
                ref="xForYBuyQuantityRef"
                v-model="xForYBuyQuantity"
                type="number"
                :min="1"
                :step="1"
                :errors="xForYBuyQuantityErrors"
                error-classes="!mt-1 !text-xs"
                :disabled="isProductFeatureDisabled"
                @update:model-value="touchXForYBuyQuantity"
              />
            </div>
            <div>
              <label class="mb-1 flex items-center gap-1 text-xs font-medium text-foreground">
                {{ t('pages.offer.reward.xForYFreeQuantityLabel') }}
                <span class="text-red-500">*</span>
              </label>
              <AppFormInput
                id="offer-xForY-freeQuantity"
                ref="xForYFreeQuantityRef"
                v-model="xForYFreeQuantity"
                type="number"
                :min="1"
                :step="1"
                :errors="xForYFreeQuantityErrors"
                error-classes="!mt-1 !text-xs"
                :disabled="isProductFeatureDisabled"
                @update:model-value="touchXForYFreeQuantity"
              />
            </div>
          </div>

          <div
            v-if="xForYSummaryHtml"
            class="flex items-center gap-2 rounded-2xl border border-primary/15 bg-primary/5 py-2 pl-4 pr-3"
          >
            <BaseIcon name="IconoirStar" class="h-4 w-4 shrink-0 text-primary" />
            <span class="text-sm text-primary" v-html="xForYSummaryHtml" />
          </div>

          <div ref="xForYItemsRef">
            <ProductAssociationBlock
              v-model:associated-products="editingReward.items"
              :is-loading="isLoadingPartners"
              :partners="productCatalogPartners"
              :readonly="isProductFeatureDisabled"
              :error-message="hasXForYItemsError ? t('pages.offer.reward.xForYItemsError') : null"
              @select-product="handleSelectTargetProduct"
              @remove-product="handleRemoveTargetProduct"
              @configure="navigateToIntegrations"
            />
          </div>
        </div>
      </template>

      <template v-if="isFreeProductReward">
        <CardTitle>{{ t('pages.reward.productAssociation.title') }}</CardTitle>
        <div class="space-y-3">
          <p class="text-xs text-foreground-secondary">
            {{ t('pages.offer.reward.selectTargetProductsHint') }}
          </p>
          <ProductAssociationBlock
            v-model:associated-products="editingReward.items"
            :is-loading="isLoadingPartners"
            :partners="productCatalogPartners"
            @select-product="handleSelectTargetProduct"
            @remove-product="handleRemoveTargetProduct"
            @configure="navigateToIntegrations"
          />
        </div>
      </template>

      <template v-if="isFreeDeliveryReward">
        <CardTitle>{{ t('pages.offer.reward.accessMinAmount') }}</CardTitle>
        <div class="space-y-3">
          <p class="text-xs text-foreground-secondary">
            {{ t('pages.offer.reward.accessMinAmountDesc') }}
          </p>
          <div class="w-32">
            <AppFormInput
              id="offer-freeDelivery-minimumOrderAmount"
              ref="freeDeliveryMinAmountRef"
              v-model="freeDeliveryMinAmount"
              type="number"
              step="0.01"
              :min="0"
              @update:model-value="touchFreeDeliveryMinAmount"
            >
              <template #icon-right>
                <div class="flex h-5 w-5 items-center justify-center text-sm text-muted-foreground">€</div>
              </template>
            </AppFormInput>
          </div>
        </div>
      </template>
    </RewardFormBlock>
  </div>
</template>
